// Curated knowledge library. Editing here is the source of truth.
// Tags map to module ids (1..4 = hormone course) so items can be surfaced
// inline. `course` is an optional discriminator for cross-course filtering
// when we introduce a second course.

export const CATEGORIES = [
  { id: "video", label: "Videos", icon: "Youtube" },
  { id: "publication", label: "Publications", icon: "FileText" },
  { id: "journal", label: "Journals", icon: "BookMarked" },
  { id: "blog", label: "Blogs", icon: "Rss" },
  { id: "supplement", label: "Supplements", icon: "Pill" },
  { id: "mind-gut", label: "Mind–Gut Research", icon: "Brain" },
  { id: "travel", label: "Travel Medicine", icon: "Plane" },
  { id: "ayurveda", label: "Ayurveda", icon: "Leaf" },
  { id: "tcm", label: "Chinese Medicine", icon: "Sparkles" },
  { id: "functional", label: "Functional Medicine", icon: "Stethoscope" },
  { id: "book", label: "Reference Texts", icon: "Library" },
];

export const RESOURCES = [
  // ====================================================================
  // EXISTING HORMONE-COURSE LIBRARY (unchanged)
  // ====================================================================

  // ---------- Videos (seeded by user) ----------------------------------
  {
    id: "v-kaiish-1",
    type: "video",
    title: "Hormone-Gut Axis — Lecture 1",
    source: "The Kaiish Institute of Functional Medicine",
    author: "Kaiish Institute",
    year: "2024",
    url: "https://youtu.be/0h1AiZdUvWk?si=3sjN8W1KulN8jzxx",
    summary:
      "Functional medicine overview of the hormone-gut axis and the role of the estrobolome in estrogen metabolism.",
    tags: [1, 2],
    course: "hormone",
  },
  {
    id: "v-kaiish-2",
    type: "video",
    title: "Estrobolome, β-Glucuronidase & Women's Health",
    source: "The Kaiish Institute of Functional Medicine",
    author: "Kaiish Institute",
    year: "2024",
    url: "https://youtu.be/xSyPjX3YlFE?si=7vSeAcOqEINM3yE5",
    summary:
      "Deeper dive into how β-glucuronidase activity drives estrogen recirculation and clinical implications.",
    tags: [2, 3],
    course: "hormone",
  },

  // ---------- Publications (peer-reviewed, referenced in the course) ---
  {
    id: "p-plottel-2011",
    type: "publication",
    title: "Microbiome and Malignancy",
    source: "Cell Host & Microbe",
    author: "Plottel CS, Blaser MJ",
    year: "2011",
    url: "https://doi.org/10.1016/j.chom.2011.10.003",
    summary:
      "The original paper that defined the estrobolome — the aggregate of enteric bacterial genes capable of metabolizing estrogens.",
    tags: [2],
    course: "hormone",
  },
  {
    id: "p-kwa-2016",
    type: "publication",
    title:
      "The Intestinal Microbiome and Estrogen Receptor–Positive Female Breast Cancer",
    source: "JNCI: Journal of the National Cancer Institute",
    author: "Kwa M, Plottel CS, Blaser MJ, Adams S",
    year: "2016",
    url: "https://doi.org/10.1093/jnci/djw029",
    summary:
      "Reviews evidence linking gut microbiome composition and β-glucuronidase activity to circulating estrogen and ER-positive breast cancer.",
    tags: [2, 3],
    course: "hormone",
  },
  {
    id: "p-ervin-2019",
    type: "publication",
    title:
      "Gut microbial β-glucuronidases reactivate estrogens as components of the estrobolome",
    source: "Journal of Biological Chemistry",
    author: "Ervin SM, Li H, Lim L, et al.",
    year: "2019",
    url: "https://doi.org/10.1074/jbc.RA119.010950",
    summary:
      "Mechanistic evidence that specific bacterial β-glucuronidases deconjugate estrogen in the gut, increasing enterohepatic recirculation.",
    tags: [2],
    course: "hormone",
  },
  {
    id: "p-baker-2017",
    type: "publication",
    title: "Estrogen–gut microbiome axis: Physiological and clinical implications",
    source: "Maturitas",
    author: "Baker JM, Al-Nakkash L, Herbst-Kralovetz MM",
    year: "2017",
    url: "https://doi.org/10.1016/j.maturitas.2017.06.025",
    summary:
      "Broad clinical review of how the gut microbiome modulates estrogen, relevant to menopause, obesity, and endometriosis.",
    tags: [1, 2, 3],
    course: "hormone",
  },

  // ---------- Journals -------------------------------------------------
  {
    id: "j-frontiers-micro",
    type: "journal",
    title: "Frontiers in Microbiology — Gut Microbiota section",
    source: "Frontiers",
    url: "https://www.frontiersin.org/journals/microbiology/sections/microbial-symbioses",
    summary:
      "Open-access journal section publishing ongoing research on gut microbiota, including estrobolome papers.",
    tags: [2, 3],
    course: "hormone",
  },
  {
    id: "j-pubmed",
    type: "journal",
    title: "PubMed — Estrobolome search",
    source: "NCBI",
    url: "https://pubmed.ncbi.nlm.nih.gov/?term=estrobolome",
    summary:
      "Up-to-date peer-reviewed literature search feed for the estrobolome across journals.",
    tags: [2],
    course: "hormone",
  },

  // ---------- Blogs ----------------------------------------------------
  {
    id: "b-precision-nutrition",
    type: "blog",
    title: "The gut microbiome and women's hormones",
    source: "Precision Nutrition",
    url: "https://www.precisionnutrition.com/research-review-the-gut-microbiome",
    summary:
      "Accessible deep-dive that translates microbiome research into practical nutrition guidance.",
    tags: [3, 4],
    course: "hormone",
  },
  {
    id: "b-lara-briden",
    type: "blog",
    title: "How to treat estrogen dominance",
    source: "Lara Briden, ND — The Period Revolutionary",
    url: "https://www.larabriden.com/the-period-revolutionary-book/",
    summary:
      "Naturopathic clinician's perspective on diet, fiber, and liver-gut support for estrogen balance.",
    tags: [1, 4],
    course: "hormone",
  },

  // ---------- Supplements ---------------------------------------------
  {
    id: "s-dim",
    type: "supplement",
    title: "Diindolylmethane (DIM) for estrogen metabolism",
    source: "Alternative Medicine Review",
    author: "Minich DM, Bland JS",
    year: "2007",
    url: "https://pubmed.ncbi.nlm.nih.gov/18069902/",
    summary:
      "Review of DIM's role in shifting estrogen metabolism toward the protective 2-OH pathway in humans.",
    tags: [4],
    course: "hormone",
  },
  {
    id: "s-calcium-d-glucarate",
    type: "supplement",
    title: "Calcium D-glucarate — β-glucuronidase inhibition",
    source: "Alternative Medicine Review",
    author: "Staff",
    year: "2002",
    url: "https://pubmed.ncbi.nlm.nih.gov/12197785/",
    summary:
      "Mechanistic and clinical review of calcium D-glucarate as a β-glucuronidase inhibitor, relevant to estrogen dominance.",
    tags: [2, 4],
    course: "hormone",
  },
  {
    id: "s-magnesium",
    type: "supplement",
    title: "Magnesium and Women's Health",
    source: "Scientifica",
    author: "Porri D, Biesalski HK, et al.",
    year: "2021",
    url: "https://doi.org/10.1155/2021/5592630",
    summary:
      "Evidence review on magnesium's role as a cofactor in Phase II liver detoxification including estrogen clearance.",
    tags: [4],
    course: "hormone",
  },
  {
    id: "s-probiotics",
    type: "supplement",
    title:
      "Probiotics and estrogen-related health: Lactobacillus & Bifidobacterium",
    source: "Nutrients",
    author: "He S, Li H, Yu Z, et al.",
    year: "2021",
    url: "https://doi.org/10.3390/nu13041266",
    summary:
      "Evidence summary on probiotic strains that support the estrobolome and healthy estrogen metabolism.",
    tags: [4],
    course: "hormone",
  },

  // ====================================================================
  // MICROBIOME COURSE LIBRARY — NEW
  // ====================================================================

  // ---------- Mind–Gut Research (Module 6 — Mood) ---------------------
  {
    id: "mg-cryan-2012",
    type: "mind-gut",
    title:
      "Mind-altering microorganisms: the impact of the gut microbiota on brain and behaviour",
    source: "Nature Reviews Neuroscience",
    author: "Cryan JF, Dinan TG",
    year: "2012",
    url: "https://doi.org/10.1038/nrn3346",
    summary:
      "Landmark review establishing that gut microbes influence the central nervous system, mood, and behaviour — the founding paper of psychobiotic research.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "mg-foster-2013",
    type: "mind-gut",
    title:
      "Gut-brain axis: how the microbiome influences anxiety and depression",
    source: "Trends in Neurosciences",
    author: "Foster JA, Neufeld KAM",
    year: "2013",
    url: "https://doi.org/10.1016/j.tins.2013.01.005",
    summary:
      "Synthesises preclinical and clinical evidence that vagal, immune, and metabolic signals from gut microbes shape anxiety and depression.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "mg-jacka-smiles-2017",
    type: "mind-gut",
    title:
      "A randomised controlled trial of dietary improvement for adults with major depression (the SMILES trial)",
    source: "BMC Medicine",
    author: "Jacka FN, O'Neil A, Opie R, et al.",
    year: "2017",
    url: "https://doi.org/10.1186/s12916-017-0791-y",
    summary:
      "First RCT to show that a Mediterranean-style dietary intervention significantly reduces depression symptoms — a foundational result for nutritional psychiatry.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "mg-messaoudi-2011",
    type: "mind-gut",
    title:
      "Beneficial psychological effects of a probiotic formulation (L. helveticus R0052 and B. longum R0175) in healthy human volunteers",
    source: "Gut Microbes",
    author: "Messaoudi M, Lalonde R, Violle N, et al.",
    year: "2011",
    url: "https://doi.org/10.4161/gmic.2.4.16108",
    summary:
      "Double-blind RCT showing reductions in anxiety and depression scores after 30 days of a specific 2-strain probiotic — the canonical 'psychobiotic' study.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "mg-mayer-2015",
    type: "mind-gut",
    title: "Gut/brain axis and the microbiota",
    source: "Journal of Clinical Investigation",
    author: "Mayer EA, Tillisch K, Gupta A",
    year: "2015",
    url: "https://doi.org/10.1172/JCI76304",
    summary:
      "Comprehensive review by the UCLA gut-brain group covering vagal afferents, neuroactive metabolites, immune signaling, and clinical implications.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "mg-berding-2023",
    type: "mind-gut",
    title:
      "Diet and the microbiota–gut–brain axis: sowing the seeds of good mental health",
    source: "Advances in Nutrition",
    author: "Berding K, Vlckova K, Marx W, et al.",
    year: "2021",
    url: "https://doi.org/10.1093/advances/nmaa181",
    summary:
      "Recent review translating gut-brain research into specific dietary patterns that support mental wellbeing.",
    tags: [],
    course: "microbiome",
  },

  // ---------- Travel Medicine ----------------------------------------
  {
    id: "tm-cdc-yellow-book",
    type: "travel",
    title: "CDC Yellow Book — Travelers' Diarrhea chapter",
    source: "Centers for Disease Control and Prevention",
    author: "CDC",
    year: "2024",
    url: "https://wwwnc.cdc.gov/travel/yellowbook/2024/preparing/travelers-diarrhea",
    summary:
      "Authoritative guidance on prevention, common pathogens, antibiotic stewardship, and post-travel care for travelers' diarrhea.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "tm-connor-2013",
    type: "travel",
    title: "Persistent travelers' diarrhea and post-infectious IBS",
    source: "Current Infectious Disease Reports",
    author: "Connor BA, Riddle MS",
    year: "2013",
    url: "https://doi.org/10.1007/s11908-013-0322-8",
    summary:
      "Reviews the well-established epidemiologic link between a single episode of travelers' diarrhea and chronic post-infectious irritable bowel syndrome.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "tm-pop-2014",
    type: "travel",
    title:
      "Diarrhea in young children from low-income countries leads to large-scale alterations in intestinal microbiota composition",
    source: "Genome Biology",
    author: "Pop M, Walker AW, Paulson J, et al.",
    year: "2014",
    url: "https://doi.org/10.1186/gb-2014-15-6-r76",
    summary:
      "Demonstrates that enteric infections cause durable, large-scale changes to the gut microbiome — relevant to travelers and to children in endemic regions.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "tm-maizels-2020",
    type: "travel",
    title: "Regulation of immunity and allergy by helminth parasites",
    source: "Allergy",
    author: "Maizels RM",
    year: "2020",
    url: "https://doi.org/10.1111/all.14138",
    summary:
      "Modern synthesis of how helminths train the immune system — central to the hygiene hypothesis and helminth-therapy research.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "tm-isgm-malaria-microbiome",
    type: "travel",
    title: "The gut microbiome and malaria — emerging interactions",
    source: "Trends in Parasitology",
    author: "Mandal RK, Schmidt NW",
    year: "2023",
    url: "https://doi.org/10.1016/j.pt.2023.07.005",
    summary:
      "Reviews evidence that gut microbiome composition influences malaria susceptibility, severity, and recovery.",
    tags: [],
    course: "microbiome",
  },

  // ---------- Ayurveda ----------------------------------------------
  {
    id: "ay-peterson-triphala-2017",
    type: "ayurveda",
    title: "Therapeutic uses of Triphala in Ayurvedic medicine",
    source: "Journal of Alternative and Complementary Medicine",
    author: "Peterson CT, Denniston K, Chopra D",
    year: "2017",
    url: "https://doi.org/10.1089/acm.2017.0083",
    summary:
      "Systematic review of preclinical and clinical evidence for Triphala — a classical Ayurvedic gut-restorative formula.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "ay-patwardhan-2014",
    type: "ayurveda",
    title:
      "Bridging Ayurveda with evidence-based scientific approaches in medicine",
    source: "EPMA Journal",
    author: "Patwardhan B",
    year: "2014",
    url: "https://doi.org/10.1186/1878-5085-5-19",
    summary:
      "Open-access framework for integrating Ayurvedic doctrine (Prakriti, Agni) with modern systems biology and microbiome research.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "ay-prasher-prakriti-2008",
    type: "ayurveda",
    title:
      "Whole genome expression and biochemical correlates of extreme constitutional types defined in Ayurveda",
    source: "Journal of Translational Medicine",
    author: "Prasher B, Negi S, Aggarwal S, et al.",
    year: "2008",
    url: "https://doi.org/10.1186/1479-5876-6-48",
    summary:
      "Foundational paper showing measurable biochemical and gene-expression differences between Ayurvedic Prakriti (constitutional) types.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "ay-niimh-charaka",
    type: "ayurveda",
    title: "Charaka Samhita — National Institute of Indian Medical Heritage",
    source: "Ministry of AYUSH, Government of India",
    author: "Classical text",
    url: "https://niimh.nic.in/ebooks/echarak/",
    summary:
      "Authoritative free online edition of the Charaka Samhita — the foundational classical Ayurvedic text on internal medicine and digestion (Agni).",
    tags: [],
    course: "microbiome",
  },

  // ---------- Traditional Chinese Medicine ---------------------------
  {
    id: "tcm-zhao-2013",
    type: "tcm",
    title: "The gut microbiota and obesity: from correlation to causality",
    source: "Nature Reviews Microbiology",
    author: "Zhao L",
    year: "2013",
    url: "https://doi.org/10.1038/nrmicro3089",
    summary:
      "Liping Zhao's influential paper bridging TCM dietary doctrine and modern microbiome science — a model for integrating the two paradigms.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "tcm-tong-berberine-2018",
    type: "tcm",
    title:
      "Structural alteration of gut microbiota during the amelioration of human type 2 diabetes with hyperlipidemia by metformin and a traditional Chinese herbal formula",
    source: "mBio",
    author: "Tong X, Xu J, Lian F, et al.",
    year: "2018",
    url: "https://doi.org/10.1128/mBio.02392-17",
    summary:
      "RCT showing TCM herbal formula (containing berberine and others) reshapes gut microbiota in T2D patients comparably to metformin.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "tcm-feng-2019",
    type: "tcm",
    title:
      "Gut microbiota: an integral moderator in health and disease — connecting TCM Spleen-Stomach theory and modern science",
    source: "Frontiers in Pharmacology",
    author: "Feng W, Ao H, Peng C",
    year: "2019",
    url: "https://doi.org/10.3389/fphar.2018.01354",
    summary:
      "Reviews how TCM's Spleen-Stomach (Pi-Wei) framework maps onto the gut microbiome and digestive function.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "tcm-chen-berberine-2016",
    type: "tcm",
    title:
      "Modulation of gut microbiota by berberine and metformin during high-fat diet-induced obesity in rats",
    source: "Scientific Reports",
    author: "Zhang X, Zhao Y, Xu J, et al.",
    year: "2015",
    url: "https://doi.org/10.1038/srep14405",
    summary:
      "Mechanistic study showing berberine — a key TCM bioactive — beneficially restructures gut microbiota under metabolic stress.",
    tags: [],
    course: "microbiome",
  },

  // ---------- Functional Medicine ------------------------------------
  {
    id: "fm-ifm-5r",
    type: "functional",
    title: "Institute for Functional Medicine — the 5R Framework",
    source: "Institute for Functional Medicine (IFM)",
    author: "IFM Faculty",
    url: "https://www.ifm.org/news-insights/the-5r-framework/",
    summary:
      "Authoritative description of the 5R framework — Remove, Replace, Reinoculate, Repair, Rebalance — used clinically for gut restoration.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "fm-bland-2017",
    type: "functional",
    title: "Defining function in the functional medicine model",
    source: "Integrative Medicine (Encinitas)",
    author: "Bland JS",
    year: "2017",
    url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5312744/",
    summary:
      "Jeffrey Bland — the founder of functional medicine — articulates the conceptual model and its evidence base.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "fm-pizzorno-glutathione",
    type: "functional",
    title: "Glutathione!",
    source: "Integrative Medicine: A Clinician's Journal",
    author: "Pizzorno J",
    year: "2014",
    url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4684116/",
    summary:
      "Joe Pizzorno's review of glutathione — central to detox pathways heavily emphasised in functional medicine protocols.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "fm-gimap",
    type: "functional",
    title: "GI-MAP — DNA-based stool testing methodology",
    source: "Diagnostic Solutions Laboratory",
    author: "DSL Clinical Education",
    url: "https://www.diagnosticsolutionslab.com/tests/gi-map",
    summary:
      "Technical overview of qPCR-based stool testing widely used in functional medicine to map pathogens, opportunists, and commensals.",
    tags: [],
    course: "microbiome",
  },

  // ---------- Reference Texts ----------------------------------------
  {
    id: "bk-murray-medical-micro",
    type: "book",
    title: "Medical Microbiology, 9th Edition",
    source: "Elsevier",
    author: "Murray PR, Rosenthal KS, Pfaller MA",
    year: "2020",
    url: "https://www.elsevier.com/books/medical-microbiology/murray/978-0-323-67322-8",
    summary:
      "The textbook this Microbiome Foundations course is built on — the standard medical-school microbiology reference.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "bk-lad-ayurveda",
    type: "book",
    title: "Ayurveda: The Science of Self-Healing",
    source: "Lotus Press",
    author: "Lad V",
    year: "1984",
    url: "https://www.ayurveda.com/about-us/dr-vasant-lad/",
    summary:
      "Vasant Lad's accessible introduction to Ayurvedic principles, doshas, and Agni (digestive fire).",
    tags: [],
    course: "microbiome",
  },
  {
    id: "bk-maciocia-foundations",
    type: "book",
    title: "The Foundations of Chinese Medicine, 3rd Edition",
    source: "Elsevier",
    author: "Maciocia G",
    year: "2015",
    url: "https://www.elsevier.com/books/the-foundations-of-chinese-medicine/maciocia/978-0-7020-5216-8",
    summary:
      "The standard English-language reference on TCM physiology — including the Spleen-Stomach (Pi-Wei) and Heart-Spleen connections relevant to mood and digestion.",
    tags: [],
    course: "microbiome",
  },
  {
    id: "bk-mayer-mind-gut",
    type: "book",
    title: "The Mind-Gut Connection",
    source: "Harper Wave",
    author: "Mayer EA",
    year: "2016",
    url: "https://www.harpercollins.com/products/the-mind-gut-connection-emeran-mayer",
    summary:
      "Emeran Mayer's accessible synthesis of two decades of UCLA gut-brain research for general readers.",
    tags: [],
    course: "microbiome",
  },
];
